import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, CardActionArea, Typography, Grid, LinearProgress,
  Chip, Divider, CircularProgress,
} from '@mui/material';
import {
  DirectionsBus, Place, AltRoute, Schedule, Tune, Groups, Assignment,
  People, WarningAmber, EditRoad,
} from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const KPIS = [
  { key: 'learnersUsingBus', label: 'Learners on bus', Icon: People,       color: '#0277bd' },
  { key: 'allocated',        label: 'Allocated to a bus', Icon: DirectionsBus, color: '#2e7d32' },
  { key: 'unallocated',      label: 'Not yet allocated', Icon: WarningAmber, color: '#e65100' },
  { key: 'buses',            label: 'Buses',           Icon: DirectionsBus, color: '#0288d1' },
  { key: 'stages',           label: 'Bus stages',      Icon: Place,        color: '#0097a7' },
  { key: 'routes',           label: 'Routes',          Icon: AltRoute,     color: '#00838f' },
];

const TILES = [
  { label: 'Learner Routes',        desc: 'Assign & change each learner\'s route', path: '/fleet/learners',  Icon: EditRoad,   color: '#0277bd' },
  { label: 'Grouping & Allocation', desc: 'Group opted-in learners, assign buses', path: '/fleet/grouping',  Icon: Groups,     color: '#01579b' },
  { label: 'Bus Manifests',         desc: 'Per-bus learner list for each trip',    path: '/fleet/manifest',  Icon: Assignment, color: '#0277bd' },
  { label: 'Buses',                 desc: 'Bus fleet (Fixed Assets) + profiles',   path: '/fleet/buses',     Icon: DirectionsBus, color: '#0288d1' },
  { label: 'Bus Stages',            desc: 'Pickup/drop-off points + monthly fee',  path: '/fleet/stages',    Icon: Place,      color: '#0097a7' },
  { label: 'Routes',                desc: 'Ordered stages along each route',        path: '/fleet/routes',    Icon: AltRoute,   color: '#00838f' },
  { label: 'Time Setup',            desc: 'Pickup/departure times per level',       path: '/fleet/time-setup',Icon: Schedule,   color: '#00695c' },
  { label: 'Transport Setup',       desc: 'One-way % and months per term',          path: '/fleet/setup',     Icon: Tune,       color: '#455a64' },
];

export default function FleetDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/fleet/stats`, { headers: hdrs() })
      .then(r => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  const maxRoute = stats?.byRoute?.reduce((m, r) => Math.max(m, r.count), 0) || 1;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, px: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <DirectionsBus sx={{ color: '#0277bd', fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0277bd' }}>Fleet Management</Typography>
      </Box>

      {/* KPI row */}
      {loading ? <CircularProgress /> : stats && (
        <Grid container spacing={2} sx={{ mb: 1 }}>
          {KPIS.map(k => (
            <Grid item xs={6} sm={4} md={2} key={k.key}>
              <Card elevation={2} sx={{ borderRadius: 2, borderLeft: `4px solid ${k.color}` }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
                    <k.Icon sx={{ color: k.color, fontSize: 18 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{k.label}</Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: k.color }}>{stats[k.key] ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Learners by route + unallocated nudge */}
      {stats && (stats.byRoute?.length > 0) && (
        <Card elevation={2} sx={{ borderRadius: 2, my: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Learners by route</Typography>
              {stats.noRoute > 0 && (
                <Chip size="small" color="warning" icon={<WarningAmber />}
                  label={`${stats.noRoute} learner(s) have no route`}
                  onClick={() => navigate('/fleet/learners')} sx={{ cursor: 'pointer' }} />
              )}
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            {stats.byRoute.map(r => (
              <Box key={r.routeCode} sx={{ mb: 1 }}>
                <Box display="flex" justifyContent="space-between" mb={0.25}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.routeName}</Typography>
                  <Typography variant="body2" color="text.secondary">{r.count}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={(r.count / maxRoute) * 100}
                  sx={{ height: 8, borderRadius: 4, bgcolor: '#eef2f5',
                    '& .MuiLinearProgress-bar': { bgcolor: r.routeCode === '(no route)' ? '#e65100' : '#0277bd' } }} />
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Navigation tiles */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {TILES.map(t => (
          <Grid item xs={12} sm={6} md={4} key={t.path}>
            <Card elevation={3} sx={{ borderRadius: 2, height: '100%' }}>
              <CardActionArea onClick={() => navigate(t.path)} sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <t.Icon sx={{ color: t.color, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{t.label}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">{t.desc}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
